import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import userActivityLogService from '../services/userActivityLogService.ts';

// 페이지 이름 매핑
const getPageDisplayName = (pathname: string): string => {
  const pageMap: { [key: string]: string } = {
    '/': '대시보드',
    '/teams': '팀 관리',
    '/team-performance': '팀 성과 관리',
    '/team-performance-refresh-demo': '팀 성과 새로고침 데모',
    '/team-performance-refresh-example': '팀 성과 새로고침 예제',
    '/team-data-demo': '팀 데이터 데모',
    '/payroll': '급여 관리',
    '/actual-sales': '실제 매출 관리',
    '/daily-report': '일일 보고서',
    '/user-management': '사용자 권한 관리',
    '/profile': '내 프로필',
    '/settings': '설정',
    '/login': '로그인',
    '/auth-test': '인증 테스트'
  };
  
  return pageMap[pathname] || pathname;
};

export const usePageTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const lastLoggedPage = useRef<string>('');
  const pageStartTime = useRef<number>(Date.now());

  useEffect(() => {
    // 사용자가 로그인되어 있고, 이전 페이지와 다른 경우에만 로그 기록
    if (user && location.pathname !== lastLoggedPage.current) {
      const logPageVisit = async () => {
        try {
          const pageDisplayName = getPageDisplayName(location.pathname);
          
          await userActivityLogService.logPageVisit(
            user.uid,
            user.email,
            user.displayName || user.email,
            location.pathname
          );
          
          console.log(`[PageTracking] Page visit logged: ${pageDisplayName} (${location.pathname})`);
          lastLoggedPage.current = location.pathname;
          pageStartTime.current = Date.now();
        } catch (error) {
          console.error('[PageTracking] Failed to log page visit:', error);
        }
      };

      // 약간의 지연을 두어 페이지가 완전히 로드된 후 로그 기록
      const timeoutId = setTimeout(logPageVisit, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, user]);

  // 페이지 떠날 때 체류 시간 기록 (선택적)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (user && lastLoggedPage.current) {
        const duration = Math.round((Date.now() - pageStartTime.current) / 1000);
        
        // 체류 시간이 의미 있는 경우에만 기록 (5초 이상)
        if (duration >= 5) {
          // navigator.sendBeacon을 사용하여 페이지 언로드 시에도 로그 전송
          try {
            const logData = {
              userId: user.uid,
              userEmail: user.email,
              userName: user.displayName || user.email,
              action: 'PAGE_DURATION',
              details: `페이지 체류 시간: ${duration}초`,
              page: lastLoggedPage.current,
              duration
            };
            
            // 간단한 로그만 기록 (복잡한 Firestore 작업은 피함)
            console.log(`[PageTracking] Page duration: ${duration}s on ${lastLoggedPage.current}`);
          } catch (error) {
            console.error('[PageTracking] Failed to log page duration:', error);
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user]);

  return {
    currentPage: location.pathname,
    pageDisplayName: getPageDisplayName(location.pathname)
  };
};

export default usePageTracking; 